// src/components/trades/components/TradeAssetsList.js
//
// Extracted from inline definition in TradeCenter.js per Tier 2 #7
// commit 7b (refactor only — no behavior change).
//
// Backend sends ENRICHED canonical player objects - just read them directly!
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import PlayerLink from '../../PlayerLink';

const TradeAssetsList = ({ assets }) => {
  if (!assets) return null;

  const picks = assets.picks || [];
  const players = assets.players || []; // Backend already sends canonical objects!
  const cash = assets.cash || 0;

  console.log('🔍 TradeAssetsList received assets:', { players, picks, cash });

  if (picks.length === 0 && players.length === 0 && cash === 0) {
    return (
      <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} text-center py-4 italic`}>
        Nothing
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Players */}
      {players.length > 0 && (
        <div>
          <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
            👤 PLAYERS ({players.length})
          </div>
          <div className="space-y-2">
            {players.map((player, idx) => {
              // ✅ Backend sent ENRICHED CANONICAL PLAYER OBJECTS - just read them!
              console.log(`🔍 PLAYER ${idx}:`, JSON.stringify(player, null, 2));
              const mlbId = player.ids?.mlb || 'unknown';
              const fullName = player.info?.full_name || 'Unknown Player';
              const position = player.info?.position || '?';
              const team = player.info?.mlb_team || 'FA';

              // ✅ Get rankings from player data
              const leagueRanks = player.rankings?.league || {};
              const positionRanks = player.rankings?.position || {};
              const mlbRanks = player.rankings?.mlb || {};
              const rankingSeason = player.rankings?.season || null; // Get season year

              return (
                <div
                  key={mlbId !== 'unknown' ? mlbId : `player-${idx}`}
                  className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.primary}`}
                >
                  {/* Top row: Image + Name/Position */}
                  <div className="flex items-start gap-3 mb-3">
                    <img
                      src={mlbId !== 'unknown'
                        ? `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_80,h_80,c_fill,g_face,q_auto:best/v1/people/${mlbId}/headshot/67/current`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=facc15&color=000&size=80`
                      }
                      alt={fullName}
                      className={`w-20 h-20 rounded-full border-2 ${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.darkFlat} object-cover flex-shrink-0`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=facc15&color=000&size=80`;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-base font-bold ${dynastyTheme.classes.text.white} truncate`}>
                        <PlayerLink mlbId={player.ids?.mlb} name={fullName} style={{ fontWeight: 700, fontSize: '1rem', color: '#f9fafb' }} />
                      </div>
                      <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                        {position} • {team}
                      </div>
                    </div>
                  </div>

                  {/* Rankings section */}
                  <div>

                    {/* ✅ Display category rankings with season year */}
                    {(Object.keys(leagueRanks).length > 0 || Object.keys(positionRanks).length > 0 || Object.keys(mlbRanks).length > 0) && (
                      <div className="space-y-1">
                        {/* League Rankings */}
                        {Object.keys(leagueRanks).length > 0 && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-green-400 font-semibold">League</span>
                              {rankingSeason && (
                                <span className="text-xs text-gray-400">({rankingSeason})</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(leagueRanks).slice(0, 6).map(([cat, rank]) => {
                                const categoryLabels = {
                                  'home_runs': 'HR',
                                  'rbi': 'RBI',
                                  'runs': 'R',
                                  'stolen_bases': 'SB',
                                  'batting_avg': 'AVG',
                                  'ops': 'OPS',
                                  'obp': 'OBP',
                                  'slg': 'SLG',
                                  'doubles': '2B',
                                  'triples': '3B',
                                  'walks': 'BB',
                                  'strikeouts': 'K'
                                };
                                const label = categoryLabels[cat] || cat.toUpperCase();
                                return (
                                  <span
                                    key={cat}
                                    className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                                      rank <= 3 ? 'bg-yellow-500/20 text-yellow-300' :
                                      rank <= 10 ? 'bg-green-500/20 text-green-300' :
                                      rank <= 20 ? 'bg-blue-500/20 text-blue-300' :
                                      'bg-gray-500/20 text-gray-300'
                                    }`}
                                    title={`League Rank: ${cat.replace('_', ' ')}`}
                                  >
                                    {label}: #{rank}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Position Rankings */}
                        {Object.keys(positionRanks).length > 0 && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-blue-400 font-semibold">Position ({position})</span>
                              {rankingSeason && (
                                <span className="text-xs text-gray-400">({rankingSeason})</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(positionRanks).slice(0, 6).map(([cat, rank]) => {
                                const categoryLabels = {
                                  'home_runs': 'HR',
                                  'rbi': 'RBI',
                                  'runs': 'R',
                                  'stolen_bases': 'SB',
                                  'batting_avg': 'AVG',
                                  'ops': 'OPS',
                                  'obp': 'OBP',
                                  'slg': 'SLG',
                                  'doubles': '2B',
                                  'triples': '3B',
                                  'walks': 'BB',
                                  'strikeouts': 'K'
                                };
                                const label = categoryLabels[cat] || cat.toUpperCase();
                                return (
                                  <span
                                    key={cat}
                                    className="px-1.5 py-0.5 rounded text-xs bg-blue-700/50 text-blue-300 font-medium"
                                    title={`Position Rank: ${cat.replace('_', ' ')}`}
                                  >
                                    {label}: #{rank}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* MLB Rankings */}
                        {Object.keys(mlbRanks).length > 0 && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-400 font-semibold">MLB</span>
                              {rankingSeason && (
                                <span className="text-xs text-gray-400">({rankingSeason})</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(mlbRanks).slice(0, 6).map(([cat, rank]) => {
                                const categoryLabels = {
                                  'home_runs': 'HR',
                                  'rbi': 'RBI',
                                  'runs': 'R',
                                  'stolen_bases': 'SB',
                                  'batting_avg': 'AVG',
                                  'ops': 'OPS',
                                  'obp': 'OBP',
                                  'slg': 'SLG',
                                  'doubles': '2B',
                                  'triples': '3B',
                                  'walks': 'BB',
                                  'strikeouts': 'K'
                                };
                                const label = categoryLabels[cat] || cat.toUpperCase();
                                return (
                                  <span
                                    key={cat}
                                    className="px-1.5 py-0.5 rounded text-xs bg-gray-700/50 text-gray-300 font-medium"
                                    title={`MLB Rank: ${cat.replace('_', ' ')}`}
                                  >
                                    {label}: #{rank}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Draft Picks */}
      {picks.length > 0 && (
        <div>
          <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
            📋 DRAFT PICKS ({picks.length})
          </div>
          <div className="space-y-2">
            {picks.map((pick, idx) => {
              const isML = pick.pick_type === 'ml';
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${isML ? 'border-purple-500' : dynastyTheme.classes.border.info}`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${isML ? 'bg-purple-600' : dynastyTheme.classes.bg.info} flex items-center justify-center font-bold ${dynastyTheme.classes.text.white} text-xs`}>
                    {isML ? 'ML' : `R${pick.round}`}
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm font-bold ${dynastyTheme.classes.text.white}`}>
                      {pick.year} {isML ? 'ML ' : ''}Round {pick.round}
                    </div>
                    {pick.original_team_name && (
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                        Originally: {pick.original_team_name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cash */}
      {cash > 0 && (
        <div>
          <div className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
            💰 CASH
          </div>
          <div className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.success}`}>
            <div className={`text-sm font-bold ${dynastyTheme.classes.text.success}`}>
              ${cash} FAAB
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeAssetsList;
